import React, {
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import AuthContext from "../AuthContext";
import { privateAxios } from "../utils/axios";
import usePermissions from "../hooks/usePermissions";
import { usePermissionContext } from "../contexts/PermissionContext";
import toast from "react-hot-toast";


function SettingPage() {
  const [limit, setLimit] = useState(10);
  const [deleteModule, setDeleteModule] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const limitOptions = [5, 10, 20, 50, 100];
  const authContext = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { userData } = usePermissions();
  const { refreshUserPermissions } = usePermissionContext();

  // -------------------------
  // Delete module data
  // -------------------------
  const handleModuleDelete = async () => {
    if (!deleteModule) {
      return toast.error("Please select a module to delete");
    }

    const confirmed = window.confirm(
      `⚠️ WARNING!\n\nThis will permanently delete ALL data from "${deleteModule}".\nThis action cannot be undone.\n\nDo you want to continue?`
    );

    if (!confirmed) return;

    try {
      setIsDeleting(true);

      const response = await privateAxios.delete(
        `/setting/delete-data?module=${deleteModule}`
      );

      if (response.data.success) {
        toast.success(`${deleteModule} data deleted successfully`);
        queryClient.invalidateQueries();
      } else {
        toast.error(response.data.message || "Failed to delete data");
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Error deleting module data"
      );
    } finally {
      setIsDeleting(false);
      setDeleteModule("");
    }
  };

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center">
      <div className="w-11/12 max-w-5xl flex flex-col gap-8 py-6">

        {/* ================= HEADER ================= */}
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">
            System Settings
          </h1>
          <p className="text-sm text-gray-500">
            Manage configuration for Motor Parts Inventory Management System
          </p>
        </div>

        {/* ================= GENERAL SETTINGS ================= */}
        <div className="bg-white border rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-medium text-gray-700 mb-4">
            General Settings
          </h2>

          {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Application Name
              </label>
              <input
                type="text"
                disabled
                value="Motor Parts Inventory Management System"
                className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Default Items Per Page
              </label>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="w-full border rounded px-3 py-2"
              >
                {limitOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div> */}
        </div>

        {/* ================= INVENTORY SETTINGS ================= */}
        <div className="bg-white border rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-medium text-gray-700 mb-4">
            Inventory Preferences
          </h2>

          <ul className="list-disc pl-5 text-sm text-gray-600 space-y-2">
            <li>Track supplier-wise purchase history</li>
            <li>Enable stock validation during purchase</li>
            <li>Prevent negative stock quantity</li>
            <li>Auto-calculate total purchase amount</li>
          </ul>
        </div>

        {/* ================= DANGER ZONE ================= */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-700 mb-2">
            ⚠️ Danger Zone
          </h2>
          <p className="text-sm text-red-600 mb-4">
            Deleting data is permanent and cannot be undone. Proceed carefully.
          </p>

          <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
            <div className="flex-1">
              <label className="block text-sm text-gray-700 mb-1">
                Select Module
              </label>
              <select
                value={deleteModule}
                onChange={(e) => setDeleteModule(e.target.value)}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">-- Select Module --</option>
                <option value="supplier">Supplier</option>
                <option value="products">Products</option>
                <option value="purchase">Purchase</option>
                <option value="expense">Expense</option>
                <option value="all">All Modules</option>
              </select>
            </div>

            <button
              onClick={handleModuleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded text-sm disabled:opacity-60"
            >
              {isDeleting ? "Deleting..." : "Delete Data"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

export default SettingPage;
